import React, { useEffect } from 'react'
import styled from 'styled-components'
import TextField from 'core-system/TextField'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment, { Moment } from 'moment'
import AddressAutocomplete, {
  Result,
} from 'core-system/Map/AddressAutocomplete/AddressAutocomplete'
import DayMonthYearInput from 'employee-platform/shared/components/DayMonthYearInput'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'

// TODO: fix this such that there is no gap between the field and the drop down
const AddressInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const isOldEnough = (date: Moment) => {
  const today = moment()
  const age = today.diff(date, 'years')
  return age >= 18
}

interface EmployeeInfoFormProps {
  setHomeAddress: (homeAddress: string) => void
  unitNumber: string
  setUnitNumber: (unitNumber: string) => void
  employeeCorpID: string
  setEmployeeCorpID: (employeeCorpID: string) => void
  dob: any
  setDOB: (dob: any) => void
  firstName: string
  setFirstName: (firstName: string) => void
  lastName: string
  setLastName: (lastName: string) => void
}

const EmployeeInfoForm = React.memo((props: EmployeeInfoFormProps) => {
  const {
    setHomeAddress,
    unitNumber,
    setUnitNumber,
    employeeCorpID,
    setEmployeeCorpID,
    dob,
    setDOB,
    firstName,
    setFirstName,
    lastName,
    setLastName,
  } = props

  const { profileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )

  const validDOBInput = dob && dob.day && dob.month && dob.year

  const dobErrorMessage =
    validDOBInput &&
    !isOldEnough(moment(`${dob.day}/${dob.month}/${dob.year}`, 'DD/MM/YYYY'))
      ? 'You must be 18 years or older to be issued a Fleet card.'
      : null
  const onValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    if (name === 'unitNumber') {
      setUnitNumber(value)
    } else if (name === 'firstName') {
      setFirstName(value)
    } else if (name === 'lastName') {
      setLastName(value)
    } else if (name === 'employeeCorpID') {
      setEmployeeCorpID(value)
    }
  }

  useEffect(() => {
    if (profileData && employeeCorpID === null) {
      setEmployeeCorpID(profileData.email.split('@')[0])
    }
  }, [profileData, employeeCorpID, setEmployeeCorpID])

  return (
    <div>
      <div>
        <div>
          <Text variant='action3' style={{ marginBottom: '0.5rem' }}>
            First Name *
          </Text>
          <TextField
            placeholder='First name'
            name='firstName'
            type='text'
            value={firstName}
            onChange={onValChange}
          />
        </div>
        <div>
          <Text
            variant='action3'
            style={{ marginBottom: '0.5rem', marginTop: '1.5rem' }}
          >
            Last Name *
          </Text>
          <TextField
            placeholder='Last name'
            name='lastName'
            type='text'
            value={lastName}
            onChange={onValChange}
          />
        </div>
      </div>
      <AddressInputContainer style={{ marginTop: '1.5rem' }}>
        <Text variant='action3' style={{ marginBottom: '0.5rem' }}>
          Home Address *
        </Text>
        {/* TODO: fix formatting issue - gap between field and drop down */}
        <AddressAutocomplete
          label={null}
          allowCustomAddress
          onActiveChange={(address: Result) => setHomeAddress(address.address)}
          hasError={false}
          customPlaceholder={
            profileData?.homeAddress || 'Enter your home address'
          }
          disabled={!!profileData?.homeAddress}
        />
        <TextField
          placeholder='Unit / Apt Number'
          name='unitNumber'
          type='text'
          value={unitNumber}
          onChange={onValChange}
          maxLength={20}
          disabled={!!profileData?.unitNumber}
        />
      </AddressInputContainer>
      <div>
        <Text
          variant='action3'
          style={{ marginBottom: '0.25rem', marginTop: '1.5rem' }}
        >
          Employee ID *
        </Text>
        <TextField
          placeholder='Employee ID'
          name='employeeCorpID'
          type='text'
          value={employeeCorpID}
          onChange={onValChange}
          disabled={!!profileData?.employeeCorpId}
        />
      </div>
      <div>
        <Text
          variant='action3'
          style={{ marginBottom: '0.25rem', marginTop: '1.5rem' }}
        >
          Date of Birth *
        </Text>
        <Text
          variant='body1'
          textColor={palette.text.placeholder}
          style={{ marginBottom: '1rem' }}
        >
          Your date of birth is required to issue a Fleet card for verification
          purposes only. We do not store this information, it is sent securely
          to our financial partners.
        </Text>
        <DayMonthYearInput
          date={dob}
          setDate={setDOB}
          errorMessage={dobErrorMessage}
        />
      </div>
    </div>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  EmployeeInfoForm.displayName = 'EmployeeInfoForm'
}

export default EmployeeInfoForm
